// pages/ContributorPhotoPage.jsx — Anamoria SPA
// v1.10 — NEW-163 Increment B (2026-10-30): a member can respond to the leader's note.
//   - Reads `?note=<group_notes id>` from location.search (it is in the URL, so it survives refresh and the
//     voice/video round trip) and quotes the note at the top of the capture + review screens
//     (RespondingToNote; copy v1.0 B-8). The note is resolved from space.groupNotes already loaded here.
//   - handleSave: the create payload carries `groupNoteId` only when the id resolves against
//     space.groupNotes (unknown → untagged; the server validates regardless, 400 INVALID_GROUP_NOTE).
//   - Share to the group with a loved-one name from ContributorSaveActions v1.4 (opts.lovedOneName):
//     PATCH {prefix}/{spaceId} { lovedOneName } (the live body-action) BEFORE the create so the
//     attribution join finds it on first render. If that PATCH fails nothing is created (the page's
//     standard error; the member can retry). Non-group / flag-off: payload byte-identical to v1.9.
//   SPA build spec v0.2 §6 (C-e confirmed at build: single create body, space GET on mount).
// E3b (Consent PR E, Oct 4, 2026): edit mode passes space/api/keepCopyExcluded to ContributorEditSharing;
//   handleKeepCopy PATCHes keepCopyExcluded (+ keepCopyShownHash on re-include); share state carries keepCopyExcluded.
// E3a (Consent PR E, Oct 4, 2026): handleSave(visibilityPref, keepCopyOpts) forwards keepCopy + keepCopyShownHash
//   from ContributorSaveActions on a first share; NOTICE_CHANGED/NOTICE_UNAVAILABLE -> plain error. Dark while the flag is off.
// v1.9 — PR-7a.2 polish: photo EDIT moves the title above the photo; the caption field is removed
//   entirely (create + edit) as unused — existing note data is left untouched on save. (2026-09-05)
// v1.8 — PR-7a.2: edit mode uses the shared compact Cancel/Save changes footer (create flow unchanged). (2026-09-05)
// v1.7 — PR-7a.1: sharing UX clarity. Sharing saves immediately; unsaved content edits keep the user here
//   (nudged), otherwise a toast confirms and drops back to the feed. Edit-mode button reads 'Save changes'. (2026-09-05)
// v1.6 — PR-7a / NS-6: edit mode now shows sharing controls (ContributorEditSharing):
//   change tier / share / make-private on the contributor's own photo, via a visibility-only PATCH
//   (independent of the content save). Group-gated. Copy = Copy v0.4 + v0.7; pull-back silent (PR-4b). (2026-09-05)
// v1.5 — PR-6 / NEW-64 private-by-default: "Save just for me" now saves a
//   PRIVATE (contributor_only) photo in THIS space for EVERY contributor via
//   the contributor create pipeline (handleSave('contributor_only')) — no
//   own-space copy. Removed the own-space owner pipeline (handleSaveJustForMe,
//   ownerApi/createApiClient) and the ownSpaceId gating on the button.
//   ContributorSaveActions now takes onSavePrivate. (2026-09-04)
// v1.4 — B1b: group-spaces two-button save (dark). When space.groupSpacesEnabled,
//   the save area shows "Save just for me" (private draft → own space via the owner
//   create pipeline, linked only) + "Share this memory" (scope picker →
//   visibilityPref owner_contributor_only|group on the contributor create). Flag
//   OFF or edit mode → unchanged single save button (byte-identical). New shared
//   <ContributorSaveActions>. Copy = Contributor Sharing Options v0.7. (July 26, 2026)
// v1.3 — Header fix: use shared LovedOneBar (May 31, 2026)
//   Root cause (same as ContributorWritePage v1.3): this page hand-rolled its
//   header against PhotoPage.module.css classes (.lovedOneBar/.bar*) that don't
//   style it, so the header rendered unstyled ("Test Space 2 / Add a photo"
//   jammed together, no avatar). Fix: render the shared <LovedOneBar> — the
//   same component ContributorRecordPage and the owner pages already use.
//     - Header → <LovedOneBar> (onBack=handleCancel, backLabel "Back to feed").
//     - Removed now-unused spaceInitial (LovedOneBar derives its own initial).
//   Scope: ONLY this file. PhotoPage.module.css and the shared component are
//   untouched. No data/save/navigation/logic change.
//
// v1.2 — Account-link access mode (May 31, 2026)
//   - Added `mode` prop ('session' default → no regression). Data layer from
//     getContributorAccess(mode, getAccessTokenSilently); contribute paths use
//     the bound prefix; nav + redirect + expiry are mode-aware; linked mode
//     uses no sessionStorage token. /media/playback stays unprefixed (shared).
//
// v1.1 — Edit mode for photo memories (May 25, 2026)
//
// Changes from v1.0:
//   - Reads location.state.editMemory on mount. If present, fetches existing
//     photo via signed URL, pre-populates title + caption, changes subtitle
//     to "Edit photo", and saves via PATCH instead of POST.
//   - Skip redirect-on-no-file when editMemory is present (existing photo).
//   - Only sends s3Key in PATCH body when photo was actually changed.
//
// v1.0 — Session 3 (April 19, 2026)
//
// Contributor photo capture page. Parallel to owner PhotoPage.jsx v2.4 but
// adapted for contributor context.
//
// Route: /contribute/:spaceId/photo
//
// CSS: Reuses PhotoPage.module.css per Session 3 Plan v1.1 decision B2
// (new component, shared CSS module). Zero CSS duplication.
//
// File arrival pattern (Safari user-gesture preservation):
//   User taps Photo in ContributorBottomNav → file picker opens in same
//   click handler → user selects file → BottomNav calls
//   navigate('/contribute/:spaceId/photo', { state: { file } }). This page
//   reads location.state.file on mount. If no file present (refresh, direct
//   URL), redirects to /contribute/:spaceId/memories.
//
// Differences from owner PhotoPage v2.4:
//   - Auth: createContributorApiClient (session token) instead of Auth0 JWT
//   - Space fetch: GET /contribute/:spaceId instead of /spaces/:id
//   - Upload URL: POST /contribute/:spaceId/upload-url (new Lambda v1.2 route,
//     verified in Step 1.6 smoke tests B-6 / B-7 / B-8). spaceId is from path;
//     body is {mimeType, mediaType} — owner route took spaceId in body.
//   - Memory create: POST /contribute/:spaceId/memories with
//     {type:'photo', s3Key, title, note} — no isPrivate (Lambda hardcodes
//     FALSE for contributor memories per decision V).
//   - No privacy toggle — contributor memories always shared.
//   - No SuccessScreen — saved pill toast + navigate to feed (S2 decision,
//     Q2-revised, matches ContributorWritePage pattern and RK-4 avoidance).
//   - No "Add another photo" flow — toast + navigate to feed; contributor
//     picks Photo again from bottom nav if they want to add another.
//   - No post-save gating (reminder/feedback) — contributor flow has none.
//   - Redirect-on-no-file → /contribute/:spaceId/memories (feed).
//   - Cancel / Remove photo → /contribute/:spaceId/memories.
//
// Structure preserved:
//   - File-as-source-of-truth + derived preview via URL.createObjectURL
//   - Validation (MIME allowlist, 10MB size limit) — same as owner
//   - Title + caption form (both optional)
//   - Same three-step save: upload-url → S3 PUT → memory POST
//
// MIME + size limits match owner PhotoPage v2.4 exactly. If the allowlist
// changes in the Lambda (ALLOWED_MIME_TYPES.photo), update both PhotoPage
// files to keep frontend in sync with backend validation.

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { getContributorAccess } from '../api/contributorAccess';
import LovedOneBar from '../components/LovedOneBar';
import ContributorSaveActions from '../components/ContributorSaveActions';
import RespondingToNote from '../components/RespondingToNote';   // v1.10 (NEW-163)
import { readNoteParam, findGroupNote } from '../utils/contributorCapture';   // v1.10 (NEW-163)
import ContributorEditSharing from '../components/ContributorEditSharing';
import ContributorEditActions from '../components/ContributorEditActions';
import styles from './PhotoPage.module.css';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB — matches owner PhotoPage + Lambda validation
const TOAST_DURATION_MS = 2500;         // matches ContributorWritePage toast timing

/* ═══════════════════════════════════════
   CONTRIBUTOR PHOTO PAGE
   ═══════════════════════════════════════ */

export default function ContributorPhotoPage({ mode = 'linked' }) {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // v1.10 (NEW-163): the group note being responded to, from ?note= (null when absent).
  const groupNoteId = readNoteParam(new URLSearchParams(location.search || ''));
  const { getAccessTokenSilently } = useAuth0();

  const navBase = '/contributing';
  const expiredMsg = 'Your session has expired. Please sign in again.';
  const access = useMemo(
    () => getContributorAccess(mode, getAccessTokenSilently),
    [mode, getAccessTokenSilently]
  );

  // v1.1: Edit mode — pre-populate from existing memory
  const editMemory = location.state?.editMemory || null;
  const isEditMode = !!editMemory;

  // ─── Space data ───
  const [space, setSpace] = useState(null);

  // ─── File + preview (canonical pattern from owner PhotoPage) ───
  const [file, setFile] = useState(location.state?.file || null);
  const [preview, setPreview] = useState(null);
  const [photoChanged, setPhotoChanged] = useState(false);

  // ─── Form state ───
  const [title, setTitle] = useState(editMemory?.title || '');
  const caption = editMemory?.note || ''; // caption field retired (unused); value preserved as note on save

  // ─── UI state ───
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showSavedPill, setShowSavedPill] = useState(false);

  // ─── Refs ───
  const fileInputRef = useRef(null);
  const toastTimerRef = useRef(null);

  /* ─── Derive preview from file (canonical URL.createObjectURL pattern) ─── */
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  /* ─── Fetch space on mount ─── */
  useEffect(() => {
    // Track B (B-2): linked-only; auth via Auth0 JWT behind ProtectedRoute.
    let cancelled = false;
    async function load() {
      try {
        const data = await access.api.get(`${access.prefix}/${spaceId}`);
        if (!cancelled) setSpace(data);
      } catch (err) {
        if (cancelled) return;
        if (err.error === 'INVALID_SESSION' || err.error === 'NO_SESSION_TOKEN' || err.status === 401) {
          setError(expiredMsg);
        } else {
          setError('Failed to load. Please go back and try again.');
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [spaceId, access, expiredMsg]);

  /* ─── Redirect if no file was passed via location.state ─── */
  // v1.1: Skip redirect in edit mode (existing photo, no file needed)
  useEffect(() => {
    if (!isEditMode && !location.state?.file) {
      navigate(`${navBase}/${spaceId}/memories`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─── v1.1: Fetch existing photo URL in edit mode ─── */
  useEffect(() => {
    if (!isEditMode || !editMemory?.s3Key) return;
    let cancelled = false;
    async function loadExistingPhoto() {
      try {
        const api = access.api;
        const data = await api.get(`/media/playback/${encodeURIComponent(editMemory.s3Key)}`);
        if (!cancelled) setPreview(data.playbackUrl);
      } catch (err) {
        console.error('Failed to load existing photo:', err);
      }
    }
    loadExistingPhoto();
    return () => { cancelled = true; };
  }, [isEditMode, editMemory]);

  /* ─── Cleanup toast timer on unmount ─── */
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  /* ─── File validation (matches owner PhotoPage) ─── */
  const validateAndSetFile = useCallback((selected) => {
    if (!ACCEPTED_TYPES.includes(selected.type)) {
      setError('Please select a JPEG, PNG, or WebP image.');
      return false;
    }
    if (selected.size > MAX_FILE_SIZE) {
      setError('Photo must be under 10 MB.');
      return false;
    }
    setError(null);
    setFile(selected);
    setPhotoChanged(true);
    return true;
  }, []);

  /* ─── "Change photo" handler ─── */
  const handleFileSelect = useCallback((e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    validateAndSetFile(selected);
  }, [validateAndSetFile]);

  const handleChangePhoto = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRemovePhoto = useCallback(() => {
    navigate(`${navBase}/${spaceId}/memories`, { replace: true });
  }, [navigate, navBase, spaceId]);

  const handleCancel = useCallback(() => {
    navigate(`${navBase}/${spaceId}/memories`);
  }, [navigate, navBase, spaceId]);

  /* ─── Save ─── */
  // E3a: keepCopyOpts = { keepCopy: true, keepCopyShownHash } from the share picker (first share), else undefined.
  const handleSave = useCallback(async (visibilityPref, keepCopyOpts) => {
    if (saving) return;
    // In create mode, file is required. In edit mode, file is optional (caption-only edit).
    if (!isEditMode && !file) return;
    setSaving(true);
    setError(null);

    try {
      // v1.10 (NEW-163): resolve the note being responded to against the space's notes; unknown → untagged.
      const respondNoteId = (!isEditMode && groupNoteId && space?.isGroupSpace === true && space?.groupSpacesEnabled === true && findGroupNote(space.groupNotes, groupNoteId)) ? groupNoteId : null;
      // v1.10 (NEW-163): loved-one name entered on the share step → save it BEFORE the create (body-action
      // PATCH on the contributor space route), so "remembering {name}" is there on first render.
      if (!isEditMode && visibilityPref === 'group' && typeof keepCopyOpts?.lovedOneName === 'string') {
        await access.api.patch(`${access.prefix}/${spaceId}`, { lovedOneName: keepCopyOpts.lovedOneName });
      }
      const api = access.api;
      let newS3Key = null;

      // Upload new photo if file was changed (or this is a new creation)
      if (file && (!isEditMode || photoChanged)) {
        const mimeType = file.type.split(';')[0];

        const uploadData = await api.post(`${access.prefix}/${spaceId}/upload-url`, {
          mimeType,
          mediaType: 'photo',
        });

        await api.putS3(uploadData.uploadUrl, file, mimeType);
        newS3Key = uploadData.s3Key;
      }

      if (isEditMode) {
        // v1.1: PATCH existing memory — only send changed fields
        const patchBody = {
          title: title.trim() || null,
          note: caption.trim() || null,
        };
        if (newS3Key) patchBody.s3Key = newS3Key;

        await api.patch(`${access.prefix}/${spaceId}/memories/${editMemory.id}`, patchBody);
      } else {
        // Create new photo memory
        await api.post(`${access.prefix}/${spaceId}/memories`, {
          type: 'photo',
          s3Key: newS3Key,
          title: title.trim() || null,
          note: caption.trim() || null,
          // B1b (dark): send the chosen share tier only when group spaces is ON;
          // omitted entirely otherwise → payload byte-identical to today.
          ...(space?.groupSpacesEnabled && typeof visibilityPref === 'string' ? { visibilityPref } : {}),
          ...(keepCopyOpts?.keepCopy === true && keepCopyOpts.keepCopyShownHash ? { keepCopy: true, keepCopyShownHash: keepCopyOpts.keepCopyShownHash } : {}),   // E3a
          ...(respondNoteId ? { groupNoteId: respondNoteId } : {}),   // v1.10 (NEW-163): only when the id resolves in space.groupNotes
        });
      }

      // Show saved pill, then navigate.
      setSaving(false);
      setShowSavedPill(true);
      toastTimerRef.current = setTimeout(() => {
        navigate(`${navBase}/${spaceId}/memories`, { replace: true });
      }, TOAST_DURATION_MS);
    } catch (err) {
      console.error('Photo save error:', err);
      if (err.error === 'INVALID_SESSION' || err.error === 'NO_SESSION_TOKEN' || err.status === 401) {
        setError(expiredMsg);
      } else if (err.error === 'NOTICE_CHANGED' || err.error === 'NOTICE_UNAVAILABLE') {
        // E3a: the keep-a-copy wording changed while they were reading (server rolled the save back).
        setError("The wording for keeping a copy has changed. Nothing was saved. Please go back and read it again.");
      } else if (err.error === 'S3_UPLOAD_FAILED') {
        setError("Upload failed. Please check your connection and try again.");
      } else {
        setError("Something didn't save. Please try again.");
      }
      setSaving(false);
    }
  }, [file, saving, title, caption, spaceId, navigate, navBase, isEditMode, editMemory, photoChanged, access, expiredMsg, space, groupNoteId]);

  // PR-7a: edit-mode sharing state (seeded from the memory being edited; updated from the PATCH response).
  const [share, setShare] = useState({ visibilityPref: editMemory?.visibilityPref, reviewStatus: editMemory?.reviewStatus, keepCopyExcluded: editMemory?.keepCopyExcluded });   // E3b
  const [sharingBusy, setSharingBusy] = useState(false);
  const [sharingNotice, setSharingNotice] = useState(false);
  const contentDirty = photoChanged || (title !== (editMemory?.title || '')) || (caption !== (editMemory?.note || ''));
  const applySharing = async (body, failMsg) => {
    if (!editMemory) return;
    setSharingBusy(true); setError(null); setSharingNotice(false);
    try {
      const res = await access.api.patch(`${access.prefix}/${spaceId}/memories/${editMemory.id}`, body);
      setShare((prev) => ({ visibilityPref: res.visibilityPref, reviewStatus: res.reviewStatus, keepCopyExcluded: res.keepCopyExcluded ?? prev.keepCopyExcluded }));   // E3b: response carries keepCopyExcluded only after a change
      if (contentDirty) {
        setSharingNotice(true);
      } else {
        setShowSavedPill(true);
        toastTimerRef.current = setTimeout(() => {
          navigate(`${navBase}/${spaceId}/memories`, { replace: true });
        }, TOAST_DURATION_MS);
      }
    } catch (err) {
      if (err.error === 'INVALID_SESSION' || err.error === 'NO_SESSION_TOKEN' || err.status === 401) setError(expiredMsg);
      else if (err.error === 'NOTICE_CHANGED' || err.error === 'NOTICE_UNAVAILABLE') setError("The wording for keeping a copy has changed. Nothing was saved. Please go back and read it again.");   // E3b
      else setError(failMsg);
    } finally { setSharingBusy(false); }
  };
  const handleShareTier = (vp) => applySharing({ visibilityPref: vp }, "We weren't able to update who can see this. When you're ready, please try again.");
  const handleMakePrivate = () => applySharing({ makePrivate: true }, "We weren't able to stop sharing that memory. When you're ready, please try again.");
  const handleKeepCopy = (excluded, shownHash) => applySharing(excluded ? { keepCopyExcluded: true } : { keepCopyExcluded: false, keepCopyShownHash: shownHash }, "We weren't able to change that. When you're ready, please try again.");   // E3b

  /* ─── Derived ─── */
  const spaceName = space?.spaceName || 'this space';
  // v1.10 (NEW-163): the quoted leader note (B-8) when responding; null otherwise / in edit mode.
  const respondQuote = (!isEditMode && groupNoteId && space?.isGroupSpace === true && space?.groupSpacesEnabled === true) ? <RespondingToNote groupNotes={space?.groupNotes} noteId={groupNoteId} leaderName={space?.ownerName} /> : null;

  /* ─── Loading state (space fetch) ─── */
  if (!space && !error) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingDot} />
        <span>Loading...</span>
      </div>
    );
  }

  /* ─── Error state (pre-load — e.g., session expired before space fetched) ─── */
  if (error && !space) {
    return (
      <div className={styles.loading}>
        <p style={{ padding: '24px', textAlign: 'center' }}>{error}</p>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════════
     PHOTO-SAVE FORM
     ═══════════════════════════════════════════════════════════ */

  return (
    <div className={styles.screen}>
      {/* Hidden file input — used by "Change photo" button */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className={styles.fileInput}
        onChange={handleFileSelect}
      />

      {/* v1.3: shared LovedOneBar (was dead PhotoPage.module.css classes). */}
      <LovedOneBar
        spaceName={spaceName}
        spacePhotoUrl={space?.photoUrl}
        subtitle={isEditMode ? 'Edit photo' : 'Add a photo'}
        onBack={handleCancel}
        backLabel="Back to feed"
      />
      {respondQuote}

      {/* Content */}
      <div className={styles.content}>
        {/* Edit: title above the photo */}
        {isEditMode && (
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              Title <span className={styles.optional}>(optional)</span>
            </label>
            <input
              type="text"
              className={styles.fieldInput}
              placeholder="Name this photo"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={75}
            />
          </div>
        )}
        {/* Photo preview */}
        {preview && (
          <div className={styles.previewContainer}>
            <img src={preview} alt="Selected photo" className={styles.previewImg} />
            <button
              className={styles.removeBtn}
              onClick={handleRemovePhoto}
              aria-label="Remove photo"
            >
              ✕
            </button>
            <button className={styles.changeHint} onClick={handleChangePhoto}>
              Tap to change
            </button>
          </div>
        )}

        {/* Title — create: below the photo (edit places it above) */}
        {!isEditMode && (
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              Title <span className={styles.optional}>(optional)</span>
            </label>
            <input
              type="text"
              className={styles.fieldInput}
              placeholder="Name this photo"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={75}
            />
          </div>
        )}


        {/* Error */}
        {error && <div className={styles.errorBanner}>{error}</div>}

        {isEditMode && space?.groupSpacesEnabled && (
          <ContributorEditSharing
            visibilityPref={share.visibilityPref}
            reviewStatus={share.reviewStatus}
            groupSpacesEnabled={space?.groupSpacesEnabled}
            saving={sharingBusy}
            onShare={handleShareTier}
            onMakePrivate={handleMakePrivate}
            space={space}
            api={access.api}
            keepCopyExcluded={share.keepCopyExcluded}
            onKeepCopyChange={handleKeepCopy}
          />
        )}

        {sharingNotice && (
          <p style={{ fontSize: 13, color: '#6b6459', margin: '4px 2px 8px' }}>
            Your edits to this photo aren&rsquo;t saved yet.
          </p>
        )}
        {/* Actions — edit: shared compact footer; create: full-width save + cancel */}
        {isEditMode ? (
          <ContributorEditActions onCancel={handleCancel} onSave={() => handleSave()} saving={saving} />
        ) : (
          <div className={styles.actions}>
            {space?.groupSpacesEnabled ? (
              <ContributorSaveActions
                saving={saving}
                onSavePrivate={() => handleSave('contributor_only')}
                onShare={(vp, kc) => handleSave(vp, kc)}
                space={space}
                api={access.api}
              />
            ) : (
              <button className={styles.btnPrimary} onClick={() => handleSave()} disabled={saving}>
                {saving ? 'Uploading...' : `Save to ${spaceName}'s space`}
              </button>
            )}
            <button className={styles.btnGhost} onClick={handleCancel} disabled={saving}>Cancel</button>
          </div>
        )}
      </div>

      {/* Saved pill toast */}
      {showSavedPill && <SavedPill />}
    </div>
  );
}

/* ═══════════════════════════════════════
   SAVED PILL TOAST
   Inline component — mirrors ContributorWritePage SavedPill exactly so
   contributor capture flows have consistent success feedback. If this
   pattern repeats more, extract to src/components/Toast.jsx (future
   refactor, not Session 3 scope).
   ═══════════════════════════════════════ */

function SavedPill() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
        background: 'var(--color-text-primary, #2d3436)',
        color: '#ffffff',
        padding: '12px 20px',
        borderRadius: '24px',
        fontSize: '14px',
        fontWeight: 500,
        fontFamily: 'var(--font-sans, "DM Sans", -apple-system, sans-serif)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        zIndex: 1000,
        pointerEvents: 'none',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          background: 'var(--color-sage, #7c9885)',
          color: '#ffffff',
          fontSize: '11px',
          lineHeight: 1,
        }}
      >
        ✓
      </span>
      Saved
    </div>
  );
}
